import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { integrationCategories } from "../data/siteContent";
import "../styles/domain-pages.css";
import "../styles/integrations.css";

const integrationMethod = [
  {
    title: "Source discovery",
    copy: "Map system owners, data formats, refresh cycles, source fields and business-critical failure points.",
    focus: [
      "System owner and business purpose",
      "Source format, access path and refresh cycle",
      "Priority data entities and known quality risks",
      "Reporting, workflow or audit dependency",
    ],
    output: "A source map that shows what needs to be connected, why it matters and which pipelines should be prioritised first.",
  },
  {
    title: "Secure ingestion",
    copy: "Connect APIs, portals, databases, SFTP files, spreadsheets and cloud storage through fit-for-purpose patterns.",
    focus: [
      "API, file, database and portal access patterns",
      "Authentication, encryption and secure transfer needs",
      "Batch, scheduled and event-driven ingestion options",
      "Landing structure for each source system",
    ],
    output: "A controlled ingestion layer using the right pattern for each source rather than forcing every system through the same connector.",
  },
  {
    title: "Validation and reconciliation",
    copy: "Check completeness, schema drift, duplicate records, business rules and source-to-target consistency.",
    focus: [
      "Completeness and duplicate checks",
      "Schema drift and source-change detection",
      "Business rule validation",
      "Source-to-target reconciliation",
    ],
    output: "Validation checks that make it clear whether a pipeline is trustworthy before downstream reporting or workflows depend on it.",
  },
  {
    title: "Operational monitoring",
    copy: "Add run logs, failure checkpoints, retry paths and ownership signals so issues can be diagnosed quickly.",
    focus: [
      "Pipeline run status and procedure logs",
      "Failure checkpoints and stop conditions",
      "Retry paths and downstream dependency control",
      "Ownership and escalation signals",
    ],
    output: "Monitoring and control metadata that shows what ran, where it failed, what stopped and what needs attention.",
  },
  {
    title: "Business activation",
    copy: "Publish governed gold datasets into semantic BI, operational reporting, audit evidence and workflow outputs.",
    focus: [
      "Gold data models and semantic BI readiness",
      "Operational reporting and exception views",
      "Audit evidence and lineage support",
      "Write-back or workflow activation where needed",
    ],
    output: "Business-ready datasets that can support decisions, reconciliation, audit evidence and operational workflows.",
  },
];

const lendingSources = [
  "Core banking portal",
  "Origination system",
  "Credit check API",
  "CRM leads, contacts and agencies",
  "GA4 analytics",
  "Static spreadsheets",
];

const bronzeLayers = [
  "Bronze_CoreBanking",
  "Bronze_Origination",
  "Bronze_CRM",
  "Bronze_GA4",
  "Bronze_Spreadsheets",
];

const silverLayers = ["Silver_Finance", "Silver_Operations", "Silver_CRM"];

const integrationControls = [
  "Master procedure orchestration",
  "Downstream dependency control",
  "Procedure run logs",
  "Failure and stop conditions",
  "Troubleshooting metadata tables",
  "Gold layer readiness checks",
];

const logoIntegrations = integrationCategories
  .flatMap((category) =>
    category.systems
      .filter((system) => system.logo)
      .map((system) => ({
        ...system,
        category: category.title,
      }))
  )
  .filter(
    (system, index, systems) =>
      systems.findIndex((item) => item.name === system.name) === index
  );

const logoIntegrationStream = [...logoIntegrations, ...logoIntegrations];

export default function IntegrationsPage() {
  const [activeMethodIndex, setActiveMethodIndex] = useState(0);
  const activeMethod = integrationMethod[activeMethodIndex];

  return (
    <div className="site-page">
      <Seo
        title="Data Integrations for Finance, Sustainability, BI and Cloud Platforms | Data Apparatus"
        description="Integration capabilities across core banking, loan origination, Azure, AWS, GCP, Snowflake, Databricks, Power BI, Salesforce, HubSpot, SAP, Xero and APIs."
      />

      <section className="page-hero integrations-hero">
        <div className="page-hero-copy">
          <span className="eyebrow">Integration capabilities</span>
          <h1>Systems we connect. Platforms we build on. Insights we deliver.</h1>
          <p>
            We connect source systems into governed, analytics-ready data foundations.
            The focus is not a logo wall; it is reliable movement, clear ownership,
            validation and reporting-ready models.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Discuss integrations
            </Link>
            <Link to="/services/data-engineering" className="secondary-btn">
              View services
            </Link>
          </div>
        </div>

        <div className="integration-ecosystem-panel hero-ecosystem-panel">
          <div className="logo-flow-window" aria-label="Systems with available logos connected into the data foundation">
            <div className="logo-flow-track">
              {logoIntegrationStream.map((system, index) => (
                <div className="logo-flow-chip" key={`${system.name}-${index}`}>
                  <span className={`logo-flow-icon${system.wide ? " wide" : ""}`}>
                    <img src={system.logo} alt="" />
                  </span>
                  <span>{system.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="ecosystem-flow-line" aria-hidden="true">
            <span>Source systems</span>
            <i />
            <strong>Controlled foundation</strong>
            <i />
            <span>Business outputs</span>
          </div>

          <div className="ecosystem-foundation-grid">
            <article className="ecosystem-foundation-card">
              <span className="architecture-lane-label">Data Apparatus integration layer</span>
              <h3>Ingest, validate, reconcile and publish.</h3>
              <p>
                APIs, files, cloud platforms, finance systems, CRMs and analytics tools
                become part of one governed operating model rather than separate reporting islands.
              </p>
              <div>
                <i>Secure ingestion</i>
                <i>Quality checks</i>
                <i>Lineage</i>
                <i>Semantic BI</i>
              </div>
            </article>

            <div className="ecosystem-output-grid">
              <article>
                <span>01</span>
                <h3>Trusted reporting</h3>
                <p>Consistent metrics, reconciled datasets and semantic BI-ready models.</p>
              </article>
              <article>
                <span>02</span>
                <h3>Operational visibility</h3>
                <p>Exception views, workflow signals and source-to-target transparency.</p>
              </article>
              <article>
                <span>03</span>
                <h3>Audit evidence</h3>
                <p>Run logs, lineage, control checks and repeatable data movement history.</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="integration-method-section">
        <div className="section-heading">
          <span className="eyebrow">How we integrate</span>
          <h2>Reliable integrations need controls, not just connectors.</h2>
          <p>
            The integration work is designed around data quality, ownership, dependency
            control and business activation so pipelines can be operated after they are built.
          </p>
        </div>
        <div className="integration-method-workbench">
          <div className="integration-step-selector" role="tablist" aria-label="Integration method steps">
            {integrationMethod.map((step, index) => (
              <button
                type="button"
                className={`integration-step-button${activeMethodIndex === index ? " active" : ""}`}
                key={step.title}
                onClick={() => setActiveMethodIndex(index)}
                role="tab"
                aria-selected={activeMethodIndex === index}
              >
                <span className="method-step-number">{String(index + 1).padStart(2, "0")}</span>
                <span>
                  <strong>{step.title}</strong>
                  <em>{step.copy}</em>
                </span>
              </button>
            ))}
          </div>

          <article className="integration-step-detail" aria-live="polite">
            <span className="architecture-lane-label">Selected integration step</span>
            <h3>{activeMethod.title}</h3>
            <p>{activeMethod.copy}</p>
            <div className="integration-detail-grid">
              <div>
                <span>Control focus</span>
                <ul>
                  {activeMethod.focus.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span>Typical output</span>
                <p>{activeMethod.output}</p>
              </div>
            </div>
            <div className="integration-progress-track" aria-hidden="true">
              {integrationMethod.map((step, index) => (
                <i
                  key={step.title}
                  className={activeMethodIndex === index ? "active" : ""}
                />
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="integration-architecture-section">
        <div className="section-heading">
          <span className="eyebrow">Example integration pattern</span>
          <h2>Controlled lending data foundation across banking, origination and CRM systems.</h2>
          <p>
            A real Azure-based pattern connecting lending source systems into bronze,
            silver and gold layers with orchestration controls, failure handling and a
            semantic BI-ready serving layer.
          </p>
        </div>

        <div className="integration-architecture-map" aria-label="Controlled lending data foundation architecture">
          <div className="architecture-map-grid">
            <article className="architecture-lane sources">
              <span className="architecture-lane-label">01 / Sources</span>
              <h3>Business systems</h3>
              <div className="architecture-chip-list">
                {lendingSources.map((source) => (
                  <i key={source}>{source}</i>
                ))}
              </div>
            </article>

            <article className="architecture-lane ingest">
              <span className="architecture-lane-label">02 / Ingestion</span>
              <h3>API and structured ingestion</h3>
              <div className="architecture-stack">
                <div>
                  <strong>Azure Function App</strong>
                  <p>CRM API, credit check API and other programmatic source pulls.</p>
                </div>
                <div>
                  <strong>Synapse pipelines</strong>
                  <p>Core banking, origination, analytics and spreadsheet ingestion.</p>
                </div>
              </div>
            </article>

            <article className="architecture-lane foundation">
              <span className="architecture-lane-label">03 / Foundation</span>
              <h3>Azure SQL medallion layers</h3>
              <div className="medallion-architecture">
                <div>
                  <strong>Bronze</strong>
                  {bronzeLayers.map((layer) => (
                    <i key={layer}>{layer}</i>
                  ))}
                </div>
                <div>
                  <strong>Silver</strong>
                  {silverLayers.map((layer) => (
                    <i key={layer}>{layer}</i>
                  ))}
                </div>
                <div>
                  <strong>Gold</strong>
                  <i>Gold lending lifecycle model</i>
                  <i>Gold finance and operations model</i>
                </div>
              </div>
            </article>

            <article className="architecture-lane controls">
              <span className="architecture-lane-label">04 / Controls</span>
              <h3>Metadata-driven orchestration</h3>
              <div className="architecture-chip-list compact">
                {integrationControls.map((control) => (
                  <i key={control}>{control}</i>
                ))}
              </div>
            </article>

            <article className="architecture-lane activation">
              <span className="architecture-lane-label">05 / Activation</span>
              <h3>BI semantic layer</h3>
              <p>
                Gold data is formalised for semantic BI, reconciliation reporting,
                operational analysis and exception visibility.
              </p>
              <div className="activation-tags">
                <span>Semantic BI</span>
                <span>Reconciliation</span>
                <span>Operational reporting</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="integration-engagement-section">
        <div className="integration-engagement-panel">
          <div>
            <span className="eyebrow">Start with the integration problem</span>
            <h2>Tell us what needs to connect, what keeps breaking, and what your team needs to trust.</h2>
            <p>
              You do not need a fully defined architecture before we speak. Bring the systems,
              files, APIs, reporting issues or workflow gaps you are dealing with. We will help
              shape the next practical step: a source map, integration review, delivery roadmap
              or implementation plan.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="primary-btn">
                Start an integration conversation
              </Link>
              <Link to="/services/data-engineering" className="secondary-btn">
                View delivery services
              </Link>
            </div>
          </div>

          <div className="integration-engagement-prompts">
            <article>
              <span>01</span>
              <h3>Bring the systems</h3>
              <p>Core applications, APIs, spreadsheets, finance systems, CRM data or cloud platforms.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Bring the friction</h3>
              <p>Manual reconciliation, failed pipelines, inconsistent numbers or unclear ownership.</p>
            </article>
            <article>
              <span>03</span>
              <h3>Leave with direction</h3>
              <p>A practical view of what to integrate first and what controls need to exist.</p>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
